import { SceneRuntime } from "./runtime/SceneRuntime";
import { rawContentFromManifest } from "./content/compileCatalog";
import {
  TuningController,
  type OverlayStorage,
} from "./content/TuningController";
import { AudioDirector } from "./engine/audio/AudioDirector";
import type { CompiledLevel } from "./engine/content/types";
import { FIXED_STEP } from "./engine/core/constants";
import type {
  GameSnapshot,
  SlimeTestApi,
  TestLevelFixture,
} from "./engine/core/types";
import { EventKind } from "./engine/events/EventQueue";
import { InputController } from "./engine/input/InputController";
import { PixiRenderer } from "./engine/render/PixiRenderer";
import { createRuleRegistry } from "./mechanics/outcomes/rules";
import { GameRecorder } from "./recording/GameRecorder";
import { HudController } from "./ui/HudController";
import { fitStage, type GameShell } from "./ui/shell";
import type {
  PanelScenario,
  PanelSituation,
  TuningPanel,
} from "./ui/TuningPanel";
import {
  telemetryConfigResponseSchema,
  type TelemetryPolicy,
} from "@slime/contracts/projects";

/** overlay 只在开发构建持久化（决策 0017 第六条）；存储不可用时按没有 overlay 处理。 */
function overlayStorage(): OverlayStorage | undefined {
  if (!import.meta.env.DEV) return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

const TELEMETRY_CONFIG_TIMEOUT_MS = 10_000;
const TELEMETRY_CONFIG_RETRIES = 10;
const TELEMETRY_CONFIG_RETRY_DELAY_MS = 1_000;

export class Game {
  private readonly ruleRegistry = createRuleRegistry();
  // 决策 0017：TuningController 必须在 catalog 之前——C 类改动走「重载生效」，持久化的 overlay
  // 要在 compileCatalog 之前叠加。catalog 因此由它编译并持有，这里只是取引用。
  private readonly tuning = new TuningController(
    this.ruleRegistry,
    overlayStorage(),
    (path, from, to) => this.recorder?.tuningChanged(path, from, to),
  );
  private readonly catalog = this.tuning.catalog;
  private readonly runtime = new SceneRuntime(this.catalog, (context) => {
    this.renderer.consumeHits(context);
    this.audio.process(context.events);
    this.recorder?.collect();
  });
  private readonly context = this.runtime.context;
  private readonly renderer = new PixiRenderer();
  private readonly gridSystem = this.runtime.gridSystem;
  private readonly pressureState = this.runtime.pressureState;
  private readonly bondState = this.runtime.bondState;
  private readonly burstSystem = this.runtime.burstSystem;
  private readonly audio = new AudioDirector();
  private hud!: HudController;
  private inputController?: InputController;
  private stopFit?: () => void;
  private stopFps?: () => void;
  private readonly lifetime = new AbortController();
  private disposed = false;
  private readonly timers = new Map<number, () => void>();
  private recorder?: GameRecorder;
  private telemetryPolicy?: TelemetryPolicy;
  private telemetryState: "disabled" | "loading" | "ready" | "failed" =
    "disabled";
  private panel?: TuningPanel;
  private hudAccumulator = 0;

  async init(shell: GameShell): Promise<void> {
    const query = new URLSearchParams(window.location.search);
    this.context.state.testMode = query.get("test") === "1";
    const telemetryDisabled = query.get("telemetry") === "false";
    if (
      import.meta.env.VITE_TELEMETRY_ENABLED !== "false" &&
      !this.context.state.testMode &&
      !telemetryDisabled
    ) {
      this.telemetryState = "loading";
      void this.loadTelemetryPolicy();
    }
    await this.renderer.init(shell.canvasHost, this.catalog);
    if (this.disposed) {
      this.renderer.destroy();
      return;
    }
    const world = this.catalog.levels[0].world;
    // 世界逻辑尺寸固定（决策 0018），画布只在舞台区域内等比缩放；size 是 C 类字段，改动走整页重载，这里读一次即可。
    this.stopFit = fitStage(
      shell.stage,
      shell.frame,
      world.width,
      world.height,
    );
    this.inputController = new InputController(
      this.renderer.app.canvas,
      this.context.input,
      world.width,
      world.height,
      (action, method) => this.recorder?.input(action, method),
    );
    this.hud = new HudController({
      onPause: () => this.togglePause(),
      onRestart: () => this.restart(),
      onNext: () => this.next(),
      onFeedback: (rating) =>
        this.recorder?.submitRecommendation(rating) ?? false,
      onMute: () => {
        this.audio.muted = !this.audio.muted;
        return this.audio.muted;
      },
    });
    document.addEventListener("pointerdown", () => this.audio.unlock(), {
      once: true,
      signal: this.lifetime.signal,
    });
    this.ensureRecorder();
    this.tuning.bind(this.context);
    if (import.meta.env.DEV) {
      window.__SLIME_TUNING__ = {
        state: () => this.tuning.state(),
        // 与 setSlimeRadius 一致地重建网格并重绘：半径变了而 maxActiveRadius 未刷新会让
        // 邻域查询用陈旧上限，测试模式没有 ticker 时更是永远不会自行恢复。
        set: (path, value) => {
          this.tuning.set(path, value);
          this.afterTuning();
        },
        reset: () => {
          this.tuning.reset();
          this.afterTuning();
        },
        baseValue: (path) => this.tuning.baseValue(path),
        reloadRequired: () => this.tuning.reloadRequired(),
        discarded: () => this.tuning.discarded,
        writeBack: () => this.writeBackTuning(),
        worldGravity: () => this.context.level.world.gravity,
        worldWidth: () => this.context.level.world.width,
        ruleParam: (instanceId, key) => {
          const rule = Object.values(this.context.level.rules)
            .flat()
            .find((candidate) => candidate.instanceId === instanceId);
          const value = rule?.params[key];
          return typeof value === "number" ? value : undefined;
        },
      };
    }
    this.loadLevel(0, true);
    if (import.meta.env.DEV) {
      // 面板是开发期产物（在关卡装载之后构造：派生读数与局面读数要读当前关卡），动态引入让它连同 Schema 遍历一起留在生产构建之外。
      const { TuningPanel } = await import("./ui/TuningPanel");
      const { mountFpsOverlay } = await import("./ui/FpsOverlay");
      if (this.disposed) return;
      this.stopFps = mountFpsOverlay(shell.frame, this.renderer.app.ticker);
      this.panel = new TuningPanel(
        {
          tuning: this.tuning,
          raw: rawContentFromManifest(),
          recorder: () => this.recorder,
          afterChange: () => this.afterTuning(),
          writeBack: () => this.writeBackTuning(),
          reload: () => window.location.reload(),
          level: () => this.context.level,
          situation: () => this.situation(),
          scenario: (kind) => {
            this.scenario(kind);
            this.afterTuning();
          },
          levels: () =>
            this.catalog.levels.map((level) => ({
              id: level.id,
              name: level.name,
            })),
          loadLevel: (id) => {
            const index = this.catalog.levels.findIndex(
              (level) => level.id === id,
            );
            if (index < 0) throw new Error(`Unknown level ${id}`);
            this.loadLevel(index, true);
            this.afterTuning();
          },
          restart: () => {
            this.restart();
            this.afterTuning();
          },
        },
        { root: shell.root, controls: shell.controls, dock: shell.dock },
      );
    }
    if (this.context.state.testMode)
      window.__SLIME_TEST_API__ = this.createTestApi();
    this.render(0);
    if (!this.context.state.testMode)
      this.renderer.app.ticker.add((ticker) =>
        this.tick(ticker.deltaMS / 1000),
      );
  }

  private tick(frameDelta: number): void {
    const dt = this.runtime.tick(frameDelta);
    this.recorder?.sample();
    this.render(dt);
  }

  private afterTuning(): void {
    this.gridSystem.rebuild(this.context);
    this.render(0);
    this.panel?.refresh();
  }

  /** 生效与落盘是两个动作（决策 0017）：把 overlay 交给 dev server 写进正式 JSON，成功后提交进 base。 */
  private async writeBackTuning(): Promise<string[]> {
    const { overlay } = this.tuning.state();
    if (Object.keys(overlay).length === 0) return [];
    const response = await fetch("/__tuning/write", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ overlay }),
    });
    const body = (await response.json().catch(() => ({}))) as {
      written?: string[];
      error?: string;
    };
    if (!response.ok) throw new Error(body.error ?? `HTTP ${response.status}`);
    this.tuning.commitToBase();
    this.panel?.refresh();
    return body.written ?? [];
  }

  private fixedUpdate(dt: number): void {
    this.runtime.fixedUpdate(dt);
  }

  private render(dt: number): void {
    this.renderer.render(this.context, dt, this.bondState, this.pressureState);
    this.hudAccumulator += dt;
    if (dt === 0 || this.hudAccumulator >= 0.08) {
      this.hudAccumulator = 0;
      this.hud.update(
        this.snapshot(),
        this.context.level.objective,
        this.context.level.kicker,
      );
    }
  }

  private loadLevel(index: number, resetScore: boolean): void {
    this.context.state.levelIndex = index;
    if (resetScore) this.resetRunMetrics();
    this.spawnLevel(this.catalog.levels[index]);
  }

  private loadFixture(fixture: TestLevelFixture): void {
    this.context.state.testMode = true;
    this.context.state.levelIndex = 0;
    this.resetRunMetrics();
    this.spawnLevel(this.catalog.compileFixture(fixture));
  }

  private resetRunMetrics(): void {
    this.context.state.score = 0;
    Object.assign(this.context.metrics, {
      chainPops: 0,
      shotsFired: 0,
      kills: 0,
      playerBursts: 0,
      maxRing: 0,
    });
  }

  private spawnLevel(level: CompiledLevel): void {
    this.ensureRecorder();
    const wasPaused = this.context.state.phase === "paused";
    const reason = !this.context.level
      ? "initial"
      : this.context.level.id === level.id
        ? "retry"
        : "next";
    this.recorder?.end("aborted", "retry");
    this.runtime.load(level);
    if (wasPaused) this.recorder?.pause(false);
    this.recorder?.begin(reason);
    // 时间轴：关卡是投放时间表，把这张表给 HUD 画出来；坐标表关卡与 Fixture 没有表，隐藏。
    const spawn = level.rules.beforePhysics.find(
      (rule) => rule.type === "core:spawn-wave",
    );
    const waves =
      (spawn?.params.waves as
        | {
            id: string;
            at: number;
            duration: number;
            rate: number;
            big?: boolean;
            note?: string;
          }[]
        | undefined) ?? [];
    this.hud.setTimeline(
      waves.map((wave) => ({
        at: wave.at,
        duration: wave.duration,
        rate: wave.rate,
        big: wave.big === true,
        note: wave.note ?? wave.id,
      })),
    );
  }

  private ensureRecorder(): void {
    if (
      this.recorder ||
      (this.telemetryState !== "loading" && this.telemetryState !== "ready") ||
      this.telemetryPolicy?.mode === "off" ||
      this.context.state.testMode ||
      !this.renderer.app.canvas
    )
      return;
    try {
      this.recorder = new GameRecorder(
        this.context,
        this.renderer.app.canvas,
        import.meta.env.VITE_TELEMETRY_URL || "/api/v3/play-records/batch",
        __GAME_VERSION__,
        () => this.tuning.state(),
        import.meta.env.VITE_TELEMETRY_PROJECT_ID || "slime-defender",
        this.telemetryState === "ready" ? this.telemetryPolicy : undefined,
        this.pressureState,
      );
    } catch {
      console.warn("Play recording unavailable");
    }
  }

  private async loadTelemetryPolicy(): Promise<void> {
    const endpoint = new URL(
      import.meta.env.VITE_TELEMETRY_URL || "/api/v3/play-records/batch",
      window.location.href,
    );
    const projectId =
      import.meta.env.VITE_TELEMETRY_PROJECT_ID || "slime-defender";
    endpoint.pathname = endpoint.pathname.replace(
      /\/batch\/?$/,
      `/projects/${encodeURIComponent(projectId)}/config`,
    );
    endpoint.search = "";
    endpoint.hash = "";
    for (
      let attempt = 0;
      attempt <= TELEMETRY_CONFIG_RETRIES && !this.disposed;
      attempt += 1
    ) {
      const controller = new AbortController();
      const timer = window.setTimeout(
        () => controller.abort(),
        TELEMETRY_CONFIG_TIMEOUT_MS,
      );
      this.timers.set(timer, () => controller.abort());
      try {
        const response = await fetch(endpoint, {
          credentials: "omit",
          signal: AbortSignal.any([controller.signal, this.lifetime.signal]),
        });
        if (response.ok) {
          const parsed = telemetryConfigResponseSchema.safeParse(
            await response.json(),
          );
          if (parsed.success && parsed.data.projectId === projectId) {
            if (this.disposed) return;
            this.telemetryPolicy = parsed.data.telemetry;
            this.telemetryState = "ready";
            if (
              this.recorder &&
              !this.recorder.resolvePolicy(this.telemetryPolicy)
            )
              this.recorder = undefined;
            return;
          }
        }
      } catch {
        // Timeout and network failures use the same bounded retry path.
      } finally {
        window.clearTimeout(timer);
        this.timers.delete(timer);
      }
      if (attempt < TELEMETRY_CONFIG_RETRIES && !this.disposed)
        await new Promise<void>((resolve) => {
          const timer = window.setTimeout(() => {
            this.timers.delete(timer);
            resolve();
          }, TELEMETRY_CONFIG_RETRY_DELAY_MS);
          this.timers.set(timer, resolve);
        });
    }
    if (this.disposed) return;
    this.failTelemetryPolicy();
    console.warn("Play recording configuration unavailable after retries");
  }

  private failTelemetryPolicy(): void {
    this.telemetryState = "failed";
    this.recorder?.discard();
    this.recorder = undefined;
  }

  destroy(mode: "normal" | "truncated" = "truncated"): void {
    if (this.disposed) return;
    this.disposed = true;
    this.lifetime.abort();
    for (const [timer, finish] of this.timers) {
      clearTimeout(timer);
      finish();
    }
    this.timers.clear();
    this.inputController?.destroy();
    this.stopFit?.();
    this.stopFps?.();
    this.hud?.destroy();
    this.panel?.destroy();
    this.recorder?.guard(() => this.recorder?.close(mode));
    this.recorder?.destroy();
    this.audio.destroy();
    this.runtime.destroy();
    this.renderer.destroy();
    if (import.meta.env.DEV) {
      delete window.__SLIME_TUNING__;
      delete window.__SLIME_TEST_API__;
    }
  }

  private togglePause(): void {
    const previous = this.context.state.phase;
    if (previous === "playing") this.runtime.pause();
    else if (previous === "paused") this.runtime.play();
    if (previous !== this.context.state.phase)
      this.recorder?.pause(this.context.state.phase === "paused");
    this.render(0);
  }
  private restart(): void {
    this.context.level.testFixture
      ? this.spawnLevel(this.context.level)
      : this.loadLevel(this.context.state.levelIndex, false);
    this.render(0);
  }
  private next(): void {
    const phase = this.context.state.phase;
    if (phase === "paused") this.togglePause();
    else {
      const target = this.runtime.nextTarget();
      if (!target || (phase === "level-clear" && this.context.state.testMode))
        return;
      if (phase === "game-over") this.restart();
      else {
        this.loadLevel(
          this.catalog.levels.findIndex((level) => level.id === target.id),
          target.resetScore,
        );
        this.render(0);
      }
    }
  }

  private snapshot(): GameSnapshot {
    const { state, slimes, drops, metrics, level } = this.context;
    let below = 0;
    for (let id = 0; id < slimes.capacity; id += 1)
      if (slimes.active[id] !== 0 && slimes.y[id] > level.world.leakLine)
        below += 1;
    return {
      ready: true,
      phase: state.phase,
      level: level.order,
      levelId: level.id,
      levelName: level.name,
      score: state.score,
      combo: state.combo,
      activeSlimes: slimes.activeCount,
      airborne: slimes.activeCount - below,
      leaked: state.leaked,
      activeDrops: drops.activeCount,
      wavePhase: this.wavePhase(),
      spawnFinished: this.context.spawn.finished,
      paceMultiplier: state.paceMultiplier,
      leakLimit: this.leakLimit(),
      levelTime: state.levelTime,
      chainPops: metrics.chainPops,
      shotsFired: metrics.shotsFired,
      slowMotion: state.slowMotion,
    };
  }

  /** 调参面板的局面读数：全部从世界状态现算，不另开统计。 */
  private situation(): PanelSituation {
    const { slimes, state, metrics, spawn, level } = this.context;
    const world = level.world;
    let sealed = 0;
    let pending = 0;
    for (let id = 0; id < slimes.capacity; id += 1) {
      if (slimes.active[id] === 0) continue;
      if (this.pressureState.pending[id] !== 0) {
        pending += 1;
        sealed += 1;
        continue;
      }
      if (
        slimes.contactDepth[id] / world.compression +
          this.bondState.degree[id] * world.bondSeal >=
        0.99
      )
        sealed += 1;
    }
    const snapshot = this.snapshot();
    return {
      airborne: snapshot.airborne,
      leaked: snapshot.leaked,
      bonds: this.bondState.count,
      sealed,
      pending,
      kills: metrics.kills,
      playerBursts: metrics.playerBursts,
      maxRing: metrics.maxRing,
      refused: spawn.refused,
      paceMultiplier: state.paceMultiplier,
      wavePhase: snapshot.wavePhase,
      spawnPaused: state.spawnPaused,
      fallFrozen: state.fallOverride === 0,
      levelTime: state.levelTime,
    };
  }

  /** 开发期场景：隔离变量用。直接写 Store 与状态，不走投放规则，因此不改变波次进度。 */
  private scenario(kind: PanelScenario): void {
    const context = this.context;
    const { slimes, state, level } = context;
    const type = (id: string): number => {
      const index = this.catalog.slimeIds.get(id);
      if (index === undefined) throw new Error(`Unknown slime ${id}`);
      return index;
    };
    const drop = (id: string, x: number, y: number) => {
      if (slimes.activeCount < slimes.capacity) slimes.spawn(x, y, type(id));
    };
    switch (kind) {
      case "cluster": {
        const x = state.playerX;
        for (let i = 0; i < 5; i += 1)
          drop(
            "core:green",
            x + (i - 2) * 22 + (i % 2) * 6,
            120 - (i % 2) * 18,
          );
        break;
      }
      case "mixed": {
        const x = state.playerX;
        drop("core:bubble", x - 24, 120);
        drop("core:green", x, 110);
        drop("core:sinker", x + 24, 120);
        break;
      }
      case "pauseSpawn":
        state.spawnPaused = !state.spawnPaused;
        break;
      case "clear":
        for (let id = 0; id < slimes.capacity; id += 1)
          if (slimes.active[id] !== 0) slimes.release(id);
        this.bondState.clear();
        break;
      case "bigWave": {
        const rule = level.rules.beforePhysics.find(
          (candidate) => candidate.type === "core:spawn-wave",
        );
        const waves =
          (rule?.params.waves as
            { id: string; at: number; rate: number }[] | undefined) ?? [];
        const big =
          waves.find((wave) => wave.id === "big") ??
          waves.reduce<{ at: number; rate: number } | undefined>(
            (best, wave) =>
              best === undefined || wave.rate > best.rate ? wave : best,
            undefined,
          );
        if (big !== undefined) {
          state.levelTime = big.at;
          context.spawn.accumulator = 0;
        }
        break;
      }
      case "freezeFall": {
        state.fallOverride = state.fallOverride === 0 ? 1 : 0;
        // 冻结时顺手清掉竖直速度，否则已有的下落速度还要靠阻尼慢慢衰减，看起来像没冻住。
        if (state.fallOverride === 0)
          for (let id = 0; id < slimes.capacity; id += 1)
            if (slimes.active[id] !== 0) slimes.vy[id] = 0;
        break;
      }
      default:
        break;
    }
  }

  private leakLimit(): number {
    const rule = this.context.level.rules.afterPhysics.find(
      (candidate) => candidate.type === "core:leak-budget",
    );
    return typeof rule?.params.max === "number" ? rule.params.max : 0;
  }

  /** 当前波次的 note。规则参数是只读内容，这里只取文字，不解释。 */
  private wavePhase(): string {
    const { spawn, level } = this.context;
    if (spawn.waveIndex < 0) return "";
    const rule = level.rules.beforePhysics.find(
      (candidate) => candidate.type === "core:spawn-wave",
    );
    const waves = rule?.params.waves;
    const wave = Array.isArray(waves)
      ? (waves[spawn.waveIndex] as { note?: unknown } | undefined)
      : undefined;
    return typeof wave?.note === "string" ? wave.note : "";
  }

  private createTestApi(): SlimeTestApi {
    return {
      snapshot: () => this.snapshot(),
      step: (frames = 1) => {
        for (let frame = 0; frame < frames; frame += 1) {
          if (this.context.state.hitstop > 0)
            this.context.state.hitstop = Math.max(
              0,
              this.context.state.hitstop - FIXED_STEP,
            );
          else if (this.context.state.phase === "playing")
            this.fixedUpdate(FIXED_STEP);
        }
        this.render(frames * FIXED_STEP);
      },
      loadLevel: (contentId) => {
        const index = this.catalog.levels.findIndex(
          (level) => level.id === contentId,
        );
        if (index < 0)
          throw new Error(`Unknown level fixture target: ${contentId}`);
        this.context.state.levelIndex = index;
        this.spawnLevel(this.catalog.levels[index]);
        this.render(0);
      },
      loadFixture: (fixture) => {
        this.loadFixture(fixture);
        this.render(0);
      },
      hitSlime: (id, dropDefinition = "core:water") => {
        const type = this.catalog.dropIds.get(dropDefinition);
        if (type === undefined)
          throw new Error(`Unknown drop fixture definition: ${dropDefinition}`);
        this.context.events.push(
          EventKind.CollisionDetected,
          -1,
          id,
          type,
          this.context.slimes.x[id] ?? 0,
          this.context.slimes.y[id] ?? 0,
          0,
          0,
          this.context.slimes.recordingEntityId[id] ?? 0,
        );
      },
      setSlimeRadius: (id, radius) => {
        if (
          !Number.isInteger(id) ||
          id < 0 ||
          id >= this.context.slimes.capacity ||
          this.context.slimes.active[id] === 0
        )
          throw new Error(`Inactive slime slot: ${id}`);
        this.context.slimes.radius[id] = radius;
        this.gridSystem.rebuild(this.context);
        this.render(0);
      },
      slimeState: (id) => {
        if (
          !Number.isInteger(id) ||
          id < 0 ||
          id >= this.context.slimes.capacity ||
          this.context.slimes.active[id] === 0
        )
          throw new Error(`Inactive slime slot: ${id}`);
        return {
          x: this.context.slimes.x[id],
          y: this.context.slimes.y[id],
          radius: this.context.slimes.radius[id],
        };
      },
      slimeVolumes: () => {
        const out: number[] = [];
        for (let id = 0; id < this.context.slimes.capacity; id += 1)
          if (this.context.slimes.active[id] !== 0)
            out.push(this.pressureState.volume[id]);
        return out;
      },
      pendingRings: () => {
        const r = this.burstSystem.rings;
        const out: number[][] = [];
        for (let i = 0; i < r.ringCount; i += 1) {
          const ring: number[] = [];
          for (let k = 0; k < r.ringSize[i]; k += 1)
            ring.push(r.members[r.ringOffset[i] + k]);
          out.push(ring);
        }
        return out;
      },
      ringWindowStart: () =>
        this.burstSystem.rings.ringCount > 0
          ? this.burstSystem.rings.ringWindowStart[0]
          : -1,
      currentStep: () => this.context.state.steps,
      pendingFlags: () => {
        const out: boolean[] = [];
        for (let id = 0; id < this.context.slimes.capacity; id += 1)
          if (this.context.slimes.active[id] !== 0)
            out.push(this.pressureState.pending[id] !== 0);
        return out;
      },
      contactDepths: () => {
        const out: number[] = [];
        for (let id = 0; id < this.context.slimes.capacity; id += 1)
          if (this.context.slimes.active[id] !== 0)
            out.push(this.context.slimes.contactDepth[id]);
        return out;
      },
      bondCount: () => this.bondState.count,
      slimeFallScales: () => {
        const out: number[] = [];
        for (let id = 0; id < this.context.slimes.capacity; id += 1)
          if (this.context.slimes.active[id] !== 0)
            out.push(this.context.slimes.fallScale[id]);
        return out;
      },
      spawnRefused: () => this.context.spawn.refused,
      slimeLinks: () => {
        const out: number[] = [];
        for (let id = 0; id < this.context.slimes.capacity; id += 1)
          if (this.context.slimes.active[id] !== 0)
            out.push(this.bondState.degree[id]);
        return out;
      },
      activeSlimeIds: () => {
        const ids: number[] = [];
        for (let id = 0; id < this.context.slimes.capacity; id += 1)
          if (this.context.slimes.active[id] !== 0) ids.push(id);
        return ids;
      },
    };
  }
}
